import { useFormik } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";
import { useMutation, useQuery } from "react-query";
import { FaExclamationCircle } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Label } from "../../components/Label";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";

export const AddRestaurantAdmin: React.FC = () => {
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      restaurantId: "",
      branchId: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Required"),
      email: Yup.string().required("Required"),
      password: Yup.string()
        .min(8, "Should not be less than 8 characters")
        .required("Required"),
      confirmPassword: Yup.string()
        .min(8, "Should not be less than 8 characters")
        .required("Required"),
      restaurantId: Yup.string().required("Required"),
      branchId: Yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      try {
        mutate(values);
      } catch (error) {
        console.log(error);
      }
    },
  });

  const { mutate, error, isLoading, data } = useMutation(async (data: any) => {
    return await axios.post("/restaurant-admin", data);
  });

  const {
    data: restaurants = [],
    isLoading: isLoadingRestaurants,
    refetch: refetchRestaurants,
    isFetching: isFetchingRestaurants,
  } = useQuery(["Get Restaurants"], async () => {
    const response = await axios.get("/restaurant");
    return response.data.restaurants;
  });

  const {
    data: branches = [],
    isLoading: isLoadingBranches,
    refetch: refetchBranches,
    isFetching: isFetchingBranches,
  } = useQuery(
    ["Get Branches", formik.values.restaurantId],
    async () => {
      const response = await axios.get(
        `/restaurant/branches/${formik.values.restaurantId}`
      );
      return response.data.branches;
    },
    {
      enabled: !!formik.values.restaurantId,
    }
  );

  useEffect(() => {
    if (!isLoading && data) {
      navigate("/dashboard/restaurants-admins");
    }
  }, [isLoading, data, navigate]);

  useEffect(() => {
    refetchRestaurants();
    if (formik.values.restaurantId) {
      refetchBranches();
    }
  }, [formik.values.restaurantId, refetchBranches, refetchRestaurants]);

  return (
    <div className="flex">
      <form
        className="flex w-8/12 flex-col gap-4 "
        onSubmit={formik.handleSubmit}
      >
        <>
          <h3 className="text-3xl font-medium py-5">Add New Restaurant User</h3>

          {error && (
            <p className="flex items-center gap-2 rounded-md bg-red-500 p-2 text-white">
              <FaExclamationCircle />
              {/* {error.message} */}
            </p>
          )}
          <div className="w-full">
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              className="w-full"
              {...formik.getFieldProps("name")}
              error={formik.touched.name ? formik.errors.name : ""}
            />
          </div>

          <div className="w-full">
            <Label htmlFor="email">Email</Label>
            <Input
              type="text"
              id="email"
              className="w-full"
              {...formik.getFieldProps("email")}
              error={formik.touched.email ? formik.errors.email : ""}
            />
          </div>

          <div className="w-full">
            <Label htmlFor="password">Password</Label>
            <Input
              type="password"
              id="password"
              className="w-full"
              {...formik.getFieldProps("password")}
              error={formik.touched.password ? formik.errors.password : ""}
            />
          </div>

          <div className="w-full">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              className="w-full"
              {...formik.getFieldProps("confirmPassword")}
              error={
                formik.touched.confirmPassword
                  ? formik.errors.confirmPassword
                  : ""
              }
            />
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="restaurantId">Restaurant</Label>
            <select
              className={`border-gray-300 border-[1px] px-5 py-3 rounded-md focus:outline-none bg-gray-50 text-gray-900 ${
                formik.touched.restaurantId
                  ? formik.errors.restaurantId
                    ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                    : ""
                  : ""
              }`}
              id="restaurantId"
              disabled={isLoadingRestaurants || isFetchingRestaurants}
              {...formik.getFieldProps("restaurantId")}
            >
              <option disabled value="">
                Choose Restaurant
              </option>
              {restaurants.map((rest: { id: number; name: string }) => (
                <option value={rest?.id}>{rest?.name}</option>
              ))}
            </select>
            {
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.restaurantId ? formik.errors.restaurantId : ""}
              </span>
            }
          </div>

          {formik.values.restaurantId &&
            !isLoadingRestaurants &&
            !isFetchingRestaurants &&
            !isLoadingBranches &&
            !isFetchingBranches && (
              <div className="flex flex-col gap-1 text-sm">
                <Label htmlFor="branchId">Branch</Label>
                <select
                  className={`border-gray-300 border-[1px] px-5 py-3 rounded-md focus:outline-none bg-gray-50 text-gray-900 ${
                    formik.touched.branchId
                      ? formik.errors.branchId
                        ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                        : ""
                      : ""
                  }`}
                  id="branchId"
                  disabled={isLoadingBranches || isFetchingBranches}
                  {...formik.getFieldProps("branchId")}
                >
                  <option disabled value="">
                    Choose Branch
                  </option>
                  {branches?.map((branch: { id: number; name: string }) => (
                    <option value={branch?.id}>{branch?.name}</option>
                  ))}
                </select>
                {
                  <span className="px-1 text-xs  text-red-500">
                    {formik.touched.branchId ? formik.errors.branchId : ""}
                  </span>
                }
              </div>
            )}

          <div className="flex gap-3">
            <Button green type="submit" loading={isLoading}>
              Add
            </Button>
            <Button
              type="button"
              lightRed
              onClick={() => navigate("/dashboard/restaurants-admins")}
            >
              Cancel
            </Button>
          </div>
        </>
      </form>
    </div>
  );
};
